import { IGetOneResponse } from '../util/Types/Generic';
import { ApiService } from './ApiService';

interface GetGoogleSlidesFromDriveResponse {
  presentations: {
    id: string;
    name: string;
  }[];
}

interface GetMicrosoftFilesResponse {
  files: {
    id: string;
    name: string;
    webUrl?: string;
    lastModifiedDateTime?: string;
  }[];
}

interface GetMicrosoftWorksheetsResponse {
  worksheets: {
    id: string;
    name: string;
    position: number;
  }[];
}

export interface GetMicrosoftFoldersResponse {
  folders: {
    id: string;
    name: string;
  }[];
}

export class PullDataService extends ApiService {
  static readonly pathName = '/pull-data';

  static readonly getWorksheetData = (body: {
    googleAccessToken: string;
    spreadsheetId: string;
    rangeFilter: string;
    sheets: number;
  }): Promise<IGetOneResponse<any>> => {
    return ApiService.post(`${this.pathName}/get-worksheet-data`, body);
  };

  static readonly getGoogleSlidesFromDrive = (body: {
    googleAccessToken: string;
  }): Promise<IGetOneResponse<GetGoogleSlidesFromDriveResponse>> => {
    return ApiService.post(
      `${this.pathName}/get-presentations-from-drive`,
      body,
    );
  };

  static readonly getGoogleSlidesData = (body: {
    googleAccessToken: string;
    presentationId: string;
    slides: string;
  }): Promise<IGetOneResponse<any>> => {
    return ApiService.post(`${this.pathName}/get-presentation-data`, body);
  };

  static readonly getMicrosoftExcelFiles = (params?: {
    search?: string;
  }): Promise<IGetOneResponse<GetMicrosoftFilesResponse>> => {
    return ApiService.post(`${this.pathName}/microsoft/list-files`, {
      fileType: 'xlsx',
      ...(params?.search ? { search: params.search } : {}),
    });
  };

  static readonly getMicrosoftExcelWorksheets = (body: {
    fileId: string;
  }): Promise<IGetOneResponse<GetMicrosoftWorksheetsResponse>> => {
    return ApiService.post(
      `${this.pathName}/microsoft/list-worksheets`,
      body,
    );
  };

  static readonly getMicrosoftExcelData = (body: {
    fileId: string;
    worksheetId: string;
    range?: string;
  }): Promise<IGetOneResponse<{ text: string }>> => {
    return ApiService.post(
      `${this.pathName}/microsoft/get-excel-data`,
      body,
    );
  };

  static readonly getMicrosoftWordFiles = (params?: {
    search?: string;
  }): Promise<IGetOneResponse<GetMicrosoftFilesResponse>> => {
    return ApiService.post(`${this.pathName}/microsoft/list-files`, {
      fileType: 'docx',
      ...(params?.search ? { search: params.search } : {}),
    });
  };

  static readonly getMicrosoftWordData = (body: {
    fileId: string;
    paragraphStart?: number;
    paragraphEnd?: number;
  }): Promise<IGetOneResponse<{ text: string }>> => {
    return ApiService.post(
      `${this.pathName}/microsoft/get-word-content`,
      body,
    );
  };

  static readonly getMicrosoftPowerPointFiles = (params?: {
    search?: string;
  }): Promise<IGetOneResponse<GetMicrosoftFilesResponse>> => {
    return ApiService.post(`${this.pathName}/microsoft/list-files`, {
      fileType: 'pptx',
      ...(params?.search ? { search: params.search } : {}),
    });
  };

  static readonly getMicrosoftPowerPointData = (body: {
    fileId: string;
    slidesFilter?: string;
  }): Promise<IGetOneResponse<{ text: string }>> => {
    return ApiService.post(
      `${this.pathName}/microsoft/get-powerpoint-content`,
      body,
    );
  };

  static readonly getMicrosoftFolders = (params?: {
    search?: string;
  }): Promise<IGetOneResponse<GetMicrosoftFoldersResponse>> => {
    return ApiService.post(`${this.pathName}/microsoft/list-folders`, {
      ...(params?.search ? { search: params.search } : {}),
    });
  };
}
